import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { ChevronDown, HelpCircle, MessageCircle, ArrowRight } from "lucide-react";

export function FAQSection() {
  const { t } = useLanguage();
  const items = t("faq.items");
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  const categoryIcons = [
    "bg-sky-500",
    "bg-cyan-500",
    "bg-violet-500",
    "bg-amber-500",
    "bg-rose-500",
    "bg-cyan-500",
    "bg-indigo-500",
  ];

  return (
    <section id="faq" data-testid="faq-section" className="py-20 sm:py-28 bg-slate-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-sky-50/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-violet-50/40 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left column - Header & CTA */}
          <div className="lg:col-span-2 lg:sticky lg:top-32 lg:self-start">
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#22A7D1]">
              {t("faq.overline")}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">
              {t("faq.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-500">
              {t("faq.description") !== "faq.description" ? t("faq.description") : ""}
            </p>

            {/* Decorative stats */}
            <div className="mt-8 grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-white border border-slate-200 p-5 text-center hover:border-sky-200 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center mx-auto mb-2">
                  <HelpCircle className="w-5 h-5 text-sky-600" />
                </div>
                <div className="text-2xl font-black text-slate-900">{Array.isArray(items) ? items.length : 7}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t("faq.questionsLabel") !== "faq.questionsLabel" ? t("faq.questionsLabel") : "Soru"}</div>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 p-5 text-center hover:border-cyan-200 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-xl bg-cyan-50 flex items-center justify-center mx-auto mb-2">
                  <MessageCircle className="w-5 h-5 text-cyan-600" />
                </div>
                <div className="text-2xl font-black text-slate-900">7/24</div>
                <div className="text-xs text-slate-500 mt-0.5">{t("faq.supportLabel") !== "faq.supportLabel" ? t("faq.supportLabel") : "Destek"}</div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-6 text-white">
              <h4 className="text-base font-semibold mb-2">
                {t("faq.ctaTitle") !== "faq.ctaTitle" ? t("faq.ctaTitle") : "Aradığınızı bulamadınız mı?"}
              </h4>
              <p className="text-sm text-slate-300 mb-4">
                {t("faq.ctaSubtitle") !== "faq.ctaSubtitle" ? t("faq.ctaSubtitle") : "Destek ekibimize ulaşın, size yardımcı olalım."}
              </p>
              <a
                href="https://wa.me/+905331631634"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-white text-slate-900 rounded-full hover:bg-slate-100 transition-colors"
                data-testid="faq-cta-whatsapp"
              >
                WhatsApp
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Right column - FAQ items */}
          <div className="lg:col-span-3 space-y-3">
            {Array.isArray(items) &&
              items.map((item, i) => {
                const isOpen = openIndex === i;
                return (
                  <div
                    key={i}
                    className={`rounded-2xl border transition-all duration-300 ${
                      isOpen
                        ? "bg-white border-sky-200 shadow-lg shadow-sky-100/30"
                        : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm"
                    }`}
                    data-testid={`faq-item-${i}`}
                  >
                    <button
                      onClick={() => toggle(i)}
                      className="w-full flex items-start gap-4 p-5 sm:p-6 text-left"
                      data-testid={`faq-trigger-${i}`}
                    >
                      {/* Number badge */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold text-white ${categoryIcons[i % categoryIcons.length]} transition-transform ${isOpen ? "scale-110" : ""}`}>
                        {String(i + 1).padStart(2, "0")}
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm sm:text-base font-semibold transition-colors ${isOpen ? "text-[#22A7D1]" : "text-slate-900"}`}>
                          {item.q}
                        </h3>

                        {/* Answer - animated */}
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            isOpen ? "max-h-96 opacity-100 mt-3" : "max-h-0 opacity-0"
                          }`}
                        >
                          <p className="text-sm leading-relaxed text-slate-500 pr-4">
                            {item.a}
                          </p>
                        </div>
                      </div>

                      {/* Chevron */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all ${isOpen ? "bg-sky-50 rotate-180" : "bg-slate-100"}`}>
                        <ChevronDown className={`w-4 h-4 transition-colors ${isOpen ? "text-sky-600" : "text-slate-400"}`} />
                      </div>
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </section>
  );
}
